import { Stack } from '@mui/material';
import {
  DatePicker,
  DatePickerProps,
  DateTimePicker,
  DateTimePickerProps,
} from '@mui/x-date-pickers';
import { useField, useFormikContext } from 'formik';
import { TextFieldProps } from 'formik-mui';
import moment, { Moment, unitOfTime } from 'moment';
import { useCallback, useMemo } from 'react';

import { DateFormat } from 'enums';

type DateOptions = {
  name: string;
  label: string;
};

type Props = {
  from: DateOptions;
  to: DateOptions;
  format: DateFormat.Date | DateFormat.Long;
  startOf?: unitOfTime.StartOf;
  endOf?: unitOfTime.StartOf;
  allowFutureDate?: boolean;
  clearable?: boolean;
  fromHelperText?: string;
  toHelperText?: string;
} & Omit<DatePickerProps<Moment>, 'onChange' | 'value'> &
  Omit<DateTimePickerProps<Moment>, 'onChange' | 'value'> &
  Partial<TextFieldProps>;

export const FormikDateRange: React.FC<Props> = ({
  from,
  to,
  format = DateFormat.Long,
  startOf,
  endOf,
  minDate,
  maxDate,
  allowFutureDate = true,
  clearable,
  fromHelperText,
  toHelperText,
  ...restProps
}) => {
  const [fromField] = useField(from.name);
  const [toField] = useField(to.name);
  const { setFieldValue } = useFormikContext();

  const showMinutes = useMemo(() => format === DateFormat.Long, [format]);

  const adjustPeriod = useMemo(
    () => (showMinutes ? 'minute' : 'day'),
    [showMinutes],
  );

  const adjustDate = useCallback(
    (date: Moment | number, adjustment: 'add' | 'subtract') => {
      if (!date) return undefined;

      return adjustment === 'add'
        ? moment(date).add(1, adjustPeriod)
        : moment(date).subtract(1, adjustPeriod);
    },
    [adjustPeriod],
  );

  const getMinDate = useCallback(
    () => adjustDate(fromField.value || minDate || 0, 'add'),
    [adjustDate, fromField.value, minDate],
  );

  const getMaxDate = useCallback(() => {
    const defaultMaxDate = allowFutureDate ? undefined : moment();

    return adjustDate(toField.value || maxDate || defaultMaxDate, 'subtract');
  }, [allowFutureDate, toField.value, maxDate, adjustDate]);

  const handleChange = useCallback(
    (name: string) => (value: Moment | null) => {
      if (value) {
        if (startOf) {
          value = moment(value).startOf(startOf);
        } else if (endOf) {
          value = moment(value).endOf(endOf);
        }
      }
      setFieldValue(name, value);
    },
    [startOf, endOf, setFieldValue],
  );

  const additionalProps = useMemo(() => {
    const slotProps = {
      textField: {
        variant: restProps?.variant || 'standard',
        size: restProps?.size,
        sx: { flex: 1 },
      },
      field: clearable ? { clearable: true } : {},
    };

    return showMinutes
      ? {
          viewRenderers: {
            hours: null,
            minutes: null,
            seconds: null,
          },
          slotProps,
        }
      : {
          slotProps,
        };
  }, [showMinutes, clearable, restProps?.size, restProps?.variant]);

  const DateComponent = useMemo(
    () => (showMinutes ? DateTimePicker : DatePicker),
    [showMinutes],
  );

  return (
    <Stack spacing={2} sx={{ width: '100%', justifyContent: 'center' }}>
      <DateComponent
        maxDate={getMaxDate()}
        label={from.label}
        format={format}
        {...additionalProps}
        {...restProps}
        value={fromField.value ? moment(fromField.value) : null}
        onChange={handleChange(from.name)}
      />
      <DateComponent
        minDate={getMinDate()}
        maxDate={allowFutureDate ? undefined : moment()}
        label={to.label}
        format={format}
        {...additionalProps}
        {...restProps}
        value={toField.value ? moment(toField.value) : null}
        onChange={handleChange(to.name)}
      />
    </Stack>
  );
};
